import { Text, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { MIRROR_PATHS, PANES, type PaneSlot, PLAN_VIEWBOX } from '~/assets/vehiclePlan';
import type { GlassArea } from '~/lib/api';
import { AREA_LABELS } from '~/lib/areas';
import { areaForPane, type DriverSide } from '~/lib/handedness';
import { useTheme } from '~/theme/ThemeProvider';

const W = 200;
// Proportional to the artwork's own viewBox (70.004 x 125.47), so nothing is stretched.
const H = Math.round((W * 125.47) / 70.004);

/**
 * Plan view of the vehicle with each glass pane tappable.
 *
 * Replaces nine enum chips with the thing the operator is actually looking at.
 *
 * **The side panes are inert until a driver side is chosen.** Not styled-as-disabled —
 * genuinely not tappable, because `areaForPane` cannot resolve them to an API area
 * without a side, and guessing would mislabel the evidence in a way nothing downstream
 * could detect. The windscreen, roof and rear screen stay live throughout: they are the
 * same window in either market.
 *
 * Badges carry the selection ORDER, so the operator can see the sequence they will be
 * asked to photograph in.
 *
 * Zones take the three design-system glass-zone tokens — selected, in-claim, off — which
 * is the same set the desk console's diagram uses, so a pane the operator marked and the
 * pane the reviewer later sees are the same colour. This artwork is kept rather than the
 * console's: the console draws TWO quarter zones per side, and this enum has one, so its
 * SVG cannot be mapped without inventing a distinction the rules engine does not have.
 */
export function VehicleGlassPlan({
  side,
  selected,
  onToggle,
}: {
  side: DriverSide | null;
  /** Selected areas, in selection order — the order drives the badge numbers. */
  selected: GlassArea[];
  onToggle: (area: GlassArea) => void;
}) {
  const { c, t, radius, space } = useTheme();
  const gated = side === null;

  const badgeFor = (slot: PaneSlot) => {
    const area = areaForPane(slot, side);
    if (!area) return null;
    const i = selected.indexOf(area);
    return i === -1 ? null : { area, order: i + 1 };
  };

  return (
    <View
      // No card. The frame stands the plan on the page ground: a bordered panel around a
      // diagram reads as a second surface competing with the cards above and below it.
      style={{ paddingVertical: space.md, alignItems: 'center' }}
    >
      <Text style={[t.monoEyebrow, { color: c.dim, textTransform: 'uppercase' }]}>Front</Text>

      {/*
        No opacity while gated. Dimming is the one signal the sunlight model destroys, and
        it says "broken" rather than "not yet". The zones are already drawn in their `off`
        colour and are genuinely not tappable; the pill below says why in words.
      */}
      <View style={{ width: W, height: H }}>
        <Svg width={W} height={H} viewBox={PLAN_VIEWBOX}>
          {/* No body silhouette: this artwork's panes describe the car on their own. */}

          {MIRROR_PATHS.map((d, i) => (
            <Path key={`m${i}`} d={d} fill={c.border2} fillOpacity={0.8} />
          ))}
          {PANES.map((pane) => {
            const area = areaForPane(pane.slot, side);
            const live = area !== null;
            const isSelected = area !== null && selected.includes(area);
            return (
              <Path
                key={pane.slot}
                // Addressable so the gate's inertness is testable, not just visual.
                testID={`pane-${pane.slot}`}
                // A tappable shape with no name is unreachable by anything that is not a
                // finger. `Path` carries these through to the native view (they are on
                // react-native-svg's CommonPathProps), so the pane announces the same
                // words the chip below it uses, and says whether it is already chosen.
                accessible={live}
                accessibilityLabel={
                  area ? `${AREA_LABELS[area]}${isSelected ? ', selected' : ''}` : undefined
                }
                d={pane.d}
                fill={isSelected ? c.zoneSelected : c.zoneOff}
                stroke={isSelected ? c.zoneSelectedStroke : c.zoneOffStroke}
                strokeWidth={isSelected ? 0.8 : 0.45}
                // Inert rather than merely dimmed: no handler at all while gated.
                onPress={live ? () => onToggle(area) : undefined}
                disabled={!live}
              />
            );
          })}
        </Svg>

        {/* Order badges, positioned by the centroids carried in the asset. */}
        {PANES.map((pane) => {
          const badge = badgeFor(pane.slot);
          if (!badge) return null;
          return (
            <View
              key={`b${pane.slot}`}
              pointerEvents="none"
              style={{
                position: 'absolute',
                left: (pane.left / 100) * W - 9,
                top: (pane.top / 100) * H - 9,
                width: 18,
                height: 18,
                borderRadius: 9,
                backgroundColor: c.inverse,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Text style={[t.monoEyebrow, { color: c.onInverse, letterSpacing: 0 }]}>
                {badge.order}
              </Text>
            </View>
          );
        })}

        {/*
          The driver side, pinned to the edge it names. It is the whole point of the gate
          above, so it sits ON the diagram rather than in a caption under it.
        */}
        {gated ? null : (
          <Text
            style={[
              t.monoEyebrow,
              {
                position: 'absolute',
                top: '46%',
                // Outside the diagram box, not offset into it: a fixed negative inset is
                // measured from the label's own edge, so a wider word slides back over
                // the artwork. `100%` anchors it to the edge whatever the word is.
                ...(side === 'right'
                  ? { left: '100%', marginLeft: space.sm }
                  : { right: '100%', marginRight: space.sm }),
                color: c.dim,
                textTransform: 'uppercase',
              },
            ]}
          >
            {side === 'right' ? 'Driver ▸' : '◂ Driver'}
          </Text>
        )}

        {gated ? (
          <View
            pointerEvents="none"
            style={{
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              alignItems: 'center',
            }}
          >
            <Text
              style={[
                t.label,
                {
                  color: c.onInverse,
                  backgroundColor: c.inverse,
                  borderRadius: radius.pill,
                  paddingHorizontal: space.md,
                  lineHeight: 32,
                  overflow: 'hidden',
                },
              ]}
            >
              Choose the driver side first
            </Text>
          </View>
        ) : null}
      </View>

      {/*
        One line, and only while it is still needed. Once a side is chosen and panes are
        being tapped the instruction has been followed, and the frame drops it.
      */}
      {gated ? (
        <Text
          style={[
            t.body,
            { color: c.dim, textAlign: 'center', paddingHorizontal: space.md, marginTop: space.sm },
          ]}
        >
          Driver-side is a different window in each market.
        </Text>
      ) : null}
    </View>
  );
}
